import type { RadialSectorItem } from '@/components/radialControlPad/root/radialControlPad.types.ts';
import { UseRadialPadRingContentProps } from '@/components/radialControlPad/root/useRadialPadRingContent.tsx';

import { radialPadLayout, radialPadTooltip } from '../radialControlPad.constants.ts';

type RadialSectorLayout = {
  sector: RadialSectorItem;
  iconX: number;
  iconY: number;
  sectorPath: string;
  tooltipLeft: number;
  tooltipTop: number;
};

/**
 * Polar layout helpers for radial pad sector paths and icon placement.
 * Adapted from Animate UI Radial Menu.
 * @see https://animate-ui.com/docs/components/community/radial-menu
 */

type Point = { x: number; y: number };

const FULL_CIRCLE = 360;

const START_ANGLE = -90;

const degToRad = (deg: number) => (deg * Math.PI) / 180;

const radToDeg = (rad: number) => (rad * 180) / Math.PI;

/** Half-angle (degrees) of the linear gap on one side at `radius`. */
const halfGapAngleDeg = (radius: number, gapPx: number) => {
  if (gapPx <= 0 || radius <= 0) {
    return 0;
  }
  return radToDeg(Math.atan(gapPx / (2 * radius)));
};

const polarToCartesian = (radius: number, angleDeg: number): Point => {
  const rad = degToRad(angleDeg);
  return {
    x: Math.cos(rad) * radius,
    y: Math.sin(rad) * radius,
  };
};

type SlicePathOptions = {
  /** Linear gap between adjacent sectors (SVG units; same width at inner and outer edge). */
  gapPx?: number;
};

const slicePath = (
  index: number,
  total: number,
  wedgeRadius: number,
  innerRadius: number,
  { gapPx = 0 }: SlicePathOptions = {}
) => {
  if (total <= 0) return '';

  if (total === 1) {
    return `
      M ${wedgeRadius} 0
      A ${wedgeRadius} ${wedgeRadius} 0 1 1 ${-wedgeRadius} 0
      A ${wedgeRadius} ${wedgeRadius} 0 1 1 ${wedgeRadius} 0
      M ${innerRadius} 0
      A ${innerRadius} ${innerRadius} 0 1 0 ${-innerRadius} 0
      A ${innerRadius} ${innerRadius} 0 1 0 ${innerRadius} 0
    `;
  }

  const anglePerSlice = FULL_CIRCLE / total;
  const midDeg = START_ANGLE + anglePerSlice * index;
  const halfSliceOuter = Math.max(anglePerSlice / 2 - halfGapAngleDeg(wedgeRadius, gapPx), 0.5);
  const halfSliceInner = Math.max(anglePerSlice / 2 - halfGapAngleDeg(innerRadius, gapPx), 0.5);
  const startDegOuter = midDeg - halfSliceOuter;
  const endDegOuter = midDeg + halfSliceOuter;
  const startDegInner = midDeg - halfSliceInner;
  const endDegInner = midDeg + halfSliceInner;

  const outerStart = polarToCartesian(wedgeRadius, startDegOuter);
  const outerEnd = polarToCartesian(wedgeRadius, endDegOuter);
  const innerStart = polarToCartesian(innerRadius, startDegInner);
  const innerEnd = polarToCartesian(innerRadius, endDegInner);
  const outerArcSpan = halfSliceOuter * 2;
  const innerArcSpan = halfSliceInner * 2;
  const outerLargeArcFlag = outerArcSpan > 180 ? 1 : 0;
  const innerLargeArcFlag = innerArcSpan > 180 ? 1 : 0;

  return `
    M ${outerStart.x} ${outerStart.y}
    A ${wedgeRadius} ${wedgeRadius} 0 ${outerLargeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}
    L ${innerEnd.x} ${innerEnd.y}
    A ${innerRadius} ${innerRadius} 0 ${innerLargeArcFlag} 0 ${innerStart.x} ${innerStart.y}
    Z
  `;
};

/** Pie wedge in the inner hole (used for center half-circles). Uses the same linear gap as ring sectors. */
const centerSlicePath = (
  index: number,
  total: number,
  outerRadius: number,
  { gapPx = 0 }: SlicePathOptions = {}
) => {
  const innerRadius = 0;

  if (total <= 0) return '';

  if (total === 1) {
    return `
      M ${outerRadius} 0
      A ${outerRadius} ${outerRadius} 0 1 1 ${-outerRadius} 0
      A ${outerRadius} ${outerRadius} 0 1 1 ${outerRadius} 0
      M ${innerRadius} 0
      A ${innerRadius} ${innerRadius} 0 1 0 ${-innerRadius} 0
      A ${innerRadius} ${innerRadius} 0 1 0 ${innerRadius} 0
    `;
  }

  return slicePath(index, total, outerRadius, gapPx, { gapPx });
};

/** Disabled when the pad or the sector is disabled; pad-level disable cannot be overridden. */
export const resolveSectorDisabled = (sectorDisabled: boolean | undefined, padDisabled?: boolean) =>
  Boolean(padDisabled) || Boolean(sectorDisabled);

/** Northward anchor shift for a sector; less on top, more on left/right (placement is always top). */
const resolveTooltipAnchorOffsetY = (midDeg: number): number => {
  const { x, y } = polarToCartesian(1, midDeg);

  if (Math.abs(x) >= Math.abs(y)) {
    return radialPadTooltip.anchorOffsetY.side;
  }

  return y >= 0 ? radialPadTooltip.anchorOffsetY.bottom : radialPadTooltip.anchorOffsetY.top;
};

export const computeSectorLayouts = ({
  ring,
  padRadius,
  sectorGapPx = radialPadLayout.sectorGapPx,
}: Pick<
  UseRadialPadRingContentProps,
  'ring' | 'padRadius' | 'sectorGapPx'
>): RadialSectorLayout[] => {
  const { sectors, outerRadius, innerRadius } = ring;
  const sliceDeg = FULL_CIRCLE / sectors.length;

  return sectors.map((sector, index) => {
    const midDeg = START_ANGLE + sliceDeg * index;
    const iconRingRadius = (outerRadius + innerRadius) / 2;
    const { x: iconX, y: iconY } = polarToCartesian(iconRingRadius, midDeg);
    const sectorPath = slicePath(index, sectors.length, outerRadius, innerRadius, {
      gapPx: sectorGapPx,
    });

    return {
      sector,
      iconX,
      iconY,
      sectorPath,
      tooltipLeft: padRadius + iconX,
      tooltipTop: padRadius + iconY - resolveTooltipAnchorOffsetY(midDeg),
    };
  });
};

export const computeCenterSectorLayouts = ({
  sectors,
  padRadius,
  outerRadius = radialPadLayout.ring.centerOuterRadius,
  sectorGapPx = radialPadLayout.sectorGapPx,
}: {
  sectors: RadialSectorItem[];
  padRadius: number;
  outerRadius?: number;
  sectorGapPx?: number;
}): RadialSectorLayout[] => {
  const sliceDeg = FULL_CIRCLE / sectors.length;
  const innerRadius = Math.max(sectorGapPx / 2, 0.5);
  const iconRingRadius = (outerRadius + innerRadius) / 2;

  return sectors.map((sector, index) => {
    const midDeg = START_ANGLE + sliceDeg * index;
    const { x: iconX, y: iconY } = polarToCartesian(iconRingRadius, midDeg);
    const sectorPath = centerSlicePath(index, sectors.length, outerRadius, {
      gapPx: sectorGapPx,
    });

    return {
      sector,
      iconX,
      iconY,
      sectorPath,
      tooltipLeft: padRadius + iconX,
      tooltipTop: padRadius + iconY - resolveTooltipAnchorOffsetY(midDeg),
    };
  });
};
